import { useQuery } from "@apollo/client";
import {
    GetPlannedInterconnectionLocationsQuery,
    GetPlannedInterconnectionLocationsQueryVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import { useMemo } from "react";
import { NiraBusId } from "types/busType";

const GET_PLANNED_INTERCONNECTION_LOCATIONS = loader(
    "src/graphql/getPlannedInterconnectionLocations.graphql"
);

// Planned Interconnection Locations (PILs) are a region-specific concept (today
// only SPP). Rather than coupling them onto the shared buses table/query, we
// fetch the small PIL set on its own and expose a nira_bus_id -> planned MW
// lookup that callers merge onto buses as needed.
//
// Returns undefined until the PIL list has loaded (or if the query errored),
// so callers can tell "not a PIL" apart from "PIL data not available yet"
// rather than treating every bus as unplanned while waiting.
const usePlannedInterconnectionLocations = (
    enabled: boolean | undefined
): ReadonlyMap<NiraBusId, number> | undefined => {
    const { data } = useQuery<
        GetPlannedInterconnectionLocationsQuery,
        GetPlannedInterconnectionLocationsQueryVariables
    >(GET_PLANNED_INTERCONNECTION_LOCATIONS, {
        skip: !enabled
    });

    return useMemo(() => {
        if (data === undefined) {
            return undefined;
        }
        return new Map(
            data.planned_interconnection_locations.map((pil) => [
                pil.nira_bus_id,
                pil.planned_mw
            ])
        );
    }, [data]);
};

export default usePlannedInterconnectionLocations;
